import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, impersonation } from "@/lib/api";
import { formatDate } from "@/lib/format";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Lock, Unlock, ShieldAlert, LogIn, DatabaseBackup, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Pagination, { paginate } from "@/components/Pagination";
import SignupRequestsCard from "@/components/SignupRequestsCard";

const DURATIONS = [
  { v: 1, label: "1 mois" },
  { v: 3, label: "3 mois" },
  { v: 6, label: "6 mois" },
  { v: 12, label: "12 mois" },
];

// Predefined subscription tiers. Selecting one auto-fills max_maisons + max_plans_per_maison.
// A DEV can then tweak the numbers manually — the Select falls back to "custom" (Personnalisé).
const PLAN_PRESETS = [
  { id: "decouverte",    label: "Découverte",  max_maisons: 3,  max_plans_per_maison: 5  },
  { id: "standard",      label: "Standard",    max_maisons: 3,  max_plans_per_maison: 10 },
  { id: "business",      label: "Business",    max_maisons: 5,  max_plans_per_maison: 10 },
  { id: "business_plus", label: "Business+",   max_maisons: 10, max_plans_per_maison: 20 },
  { id: "pro",           label: "Pro",         max_maisons: 20, max_plans_per_maison: 20 },
];

const findMatchingPreset = (maisons, plans) => {
  const m = Number(maisons), p = Number(plans);
  return PLAN_PRESETS.find((x) => x.max_maisons === m && x.max_plans_per_maison === p)?.id || "custom";
};

const empty = { nom: "", max_maisons: 3, max_users: 2, max_plans_per_maison: 5, duration_months: 12, status: "active" };

export default function Dev() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [confirmId, setConfirmId] = useState(null);
  const [page, setPage] = useState(1);
  const [credentials, setCredentials] = useState(null); // {nom, email, password}
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const navigate = useNavigate();

  const pag = paginate(items, page);

  const onAccess = (s) => {
    if (s.status !== "active") {
      toast.error("Société fermée. Réouvrez-la avant d'y accéder.");
      return;
    }
    impersonation.start(s.id, s.nom);
    toast.success(`Accès en tant que « ${s.nom} »`);
    navigate("/");
  };

  const load = async () => {
    try {
      const { data } = await api.get("/societies");
      setItems(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
  };
  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      nom: form.nom.trim(),
      max_maisons: Number(form.max_maisons || 1),
      max_users: Number(form.max_users || 1),
      max_plans_per_maison: Number(form.max_plans_per_maison || 1),
      duration_months: Number(form.duration_months || 12),
      status: form.status,
    };
    try {
      if (editing) {
        await api.put(`/societies/${editing}`, payload);
        toast.success("Société modifiée");
      } else {
        const { data } = await api.post("/societies", payload);
        toast.success("Société créée");
        if (data?.admin_email) {
          setCredentials({ nom: data.nom, email: data.admin_email, password: data.admin_password });
        }
      }
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
  };

  const onEdit = (s) => { setEditing(s.id); setForm({ nom: s.nom, max_maisons: s.max_maisons, max_users: s.max_users ?? 2, max_plans_per_maison: s.max_plans_per_maison ?? 20, duration_months: s.duration_months, status: s.status }); setOpen(true); };
  const onClose = async (s) => {
    try { await api.post(`/societies/${s.id}/close`); toast.success("Société fermée"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
  };
  const onOpen = async (s) => {
    try { await api.post(`/societies/${s.id}/open`); toast.success("Société réouverte"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
  };
  const onDelete = async () => {
    try { await api.delete(`/societies/${confirmId}`); toast.success("Société supprimée"); setConfirmId(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Suppression échouée"); }
  };

  const onRestore = async () => {
    if (!restoreFile) { toast.error("Sélectionnez un fichier .gz"); return; }
    setRestoring(true);
    setRestoreResult(null);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      const { data } = await api.post("/dev/restore-database", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 15 * 60 * 1000,
      });
      setRestoreResult(data);
      toast.success("Base restaurée. Reconnectez-vous.");
      // Force logout after successful restore — user credentials may have changed.
      setTimeout(() => {
        try { localStorage.clear(); } catch (_) { /* noop */ }
        window.location.href = "/login";
      }, 3500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restauration échouée");
      setRestoring(false);
      setRestoreConfirm(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Espace DEV — Multi-société"
        subtitle="Création, modification et gestion du cycle de vie des sociétés clientes. Auto-fermeture à l'échéance d'abonnement."
        testid="page-dev"
        accent="amber"
        actions={
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Button
              variant="outline"
              onClick={() => { setRestoreFile(null); setRestoreConfirm(false); setRestoreResult(null); setRestoreOpen(true); }}
              className="border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:text-amber-900"
              data-testid="btn-restore-db"
            >
              <DatabaseBackup className="w-4 h-4 mr-2" /> Restaurer la base
            </Button>
            <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-900/20" data-testid="btn-add-society">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle société
            </Button>
          </div>
        }
      />

      <div className="mb-6 rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
        <ShieldAlert className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900">
          <strong>Espace réservé au rôle DEV.</strong> Les utilisateurs d'une société fermée peuvent toujours se connecter en lecture seule (un bandeau rouge indique le statut). La fermeture est automatique à la date d'expiration et manuelle via les boutons ci-dessous.
        </div>
      </div>

      <SignupRequestsCard onApproved={load} />

      <Card className="rounded-xl border border-slate-200 shadow-sm overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Société</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Statut</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Maisons</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Plans/maison</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Utilisateurs (admin & agent)</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total comptes</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Ouverture</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Expire le</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-12">Aucune société enregistrée.</TableCell></TableRow>
            )}
            {pag.rows.map((s) => (
              <TableRow key={s.id} className="hover:bg-slate-50/60" data-testid={`row-society-${s.id}`}>
                <TableCell className="font-medium text-slate-900">{s.nom}</TableCell>
                <TableCell>
                  {s.status === "active"
                    ? <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100">Active</Badge>
                    : <Badge className="bg-rose-100 text-rose-800 border border-rose-200 hover:bg-rose-100">Fermée</Badge>}
                </TableCell>
                <TableCell className="text-slate-700">
                  <span className={`font-medium ${s.maison_count >= s.max_maisons ? "text-rose-700" : "text-slate-900"}`}>{s.maison_count}</span>
                  <span className="text-slate-400">/{s.max_maisons}</span>
                </TableCell>
                <TableCell className="text-slate-700" data-testid={`cell-max-plans-${s.id}`}>
                  <span className="font-medium text-slate-900">{s.max_plans_per_maison ?? 20}</span>
                  <span className="text-xs text-slate-400 ml-1">max</span>
                </TableCell>
                <TableCell className="text-slate-700">
                  <span className={`font-medium ${(s.user_capped_count ?? 0) >= (s.max_users ?? 2) ? "text-rose-700" : "text-slate-900"}`} data-testid={`cell-user-count-${s.id}`}>{s.user_capped_count ?? 0}</span>
                  <span className="text-slate-400">/{s.max_users ?? 2}</span>
                </TableCell>
                <TableCell className="text-slate-700">{s.user_count}</TableCell>
                <TableCell className="text-slate-600">{formatDate(s.opened_at)}</TableCell>
                <TableCell className="text-slate-600">{formatDate(s.expires_at)} <span className="text-xs text-slate-400">({s.duration_months} mois)</span></TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    onClick={() => onAccess(s)}
                    title="Se connecter à cette société"
                    data-testid={`btn-access-${s.id}`}
                    className="mr-1 h-8 px-2.5 bg-teal-700 hover:bg-teal-800 text-white text-xs font-semibold gap-1 disabled:opacity-50"
                    disabled={s.status !== "active"}
                  >
                    <LogIn className="w-3.5 h-3.5" /> Accéder
                  </Button>
                  {s.status === "active" ? (
                    <Button size="sm" variant="ghost" onClick={() => onClose(s)} title="Fermer" data-testid={`btn-close-${s.id}`}>
                      <Lock className="w-4 h-4 text-rose-600" />
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => onOpen(s)} title="Réouvrir" data-testid={`btn-open-${s.id}`}>
                      <Unlock className="w-4 h-4 text-emerald-700" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => onEdit(s)} data-testid={`btn-edit-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmId(s.id)} data-testid={`btn-delete-${s.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Pagination {...pag} onChange={setPage} testid="pagination-societies" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dialog-society">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifier la société" : "Nouvelle société"}</DialogTitle></DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nom de la société *</Label>
              <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} required data-testid="input-society-nom" />
            </div>
            <div className="space-y-2 rounded-lg border border-teal-200 bg-teal-50/60 p-3">
              <Label className="text-teal-900 font-semibold">Formule prédéfinie</Label>
              <Select
                value={findMatchingPreset(form.max_maisons, form.max_plans_per_maison)}
                onValueChange={(pid) => {
                  const p = PLAN_PRESETS.find((x) => x.id === pid);
                  if (p) setForm({ ...form, max_maisons: p.max_maisons, max_plans_per_maison: p.max_plans_per_maison });
                }}
              >
                <SelectTrigger data-testid="select-society-plan"><SelectValue placeholder="Choisir une formule" /></SelectTrigger>
                <SelectContent>
                  {PLAN_PRESETS.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`plan-${p.id}`}>
                      {p.label} — {p.max_maisons} maison{p.max_maisons > 1 ? "s" : ""} × {p.max_plans_per_maison} plans/maison
                    </SelectItem>
                  ))}
                  <SelectItem value="custom" disabled data-testid="plan-custom">Personnalisé (ajuster les champs ci-dessous)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-teal-800/80">
                Sélectionnez une formule pour pré-remplir automatiquement les limites, puis ajustez-les si besoin.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Limite de maisons *</Label>
                <Input type="number" min="1" value={form.max_maisons} onChange={(e) => setForm({ ...form, max_maisons: e.target.value })} data-testid="input-society-max" />
              </div>
              <div className="space-y-2">
                <Label>Limite utilisateurs (admin & agent) *</Label>
                <Input type="number" min="1" value={form.max_users} onChange={(e) => setForm({ ...form, max_users: e.target.value })} data-testid="input-society-max-users" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Limite totale de plans par maison *</Label>
              <Input
                type="number"
                min="1"
                value={form.max_plans_per_maison}
                onChange={(e) => setForm({ ...form, max_plans_per_maison: e.target.value })}
                data-testid="input-society-max-plans"
              />
              <p className="text-xs text-slate-500">
                Somme des <code>nb_plans</code> de toutes les architectures d'une maison (T3 × 4 + T4 × 2 = 6 plans).
                Toute création/modification de maison dépassant ce quota sera refusée.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Durée d'abonnement</Label>
              <Select value={String(form.duration_months)} onValueChange={(v) => setForm({ ...form, duration_months: Number(v) })}>
                <SelectTrigger data-testid="select-society-duration"><SelectValue /></SelectTrigger>
                <SelectContent>{DURATIONS.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="btn-save-society">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent data-testid="dialog-confirm-delete-society">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-rose-700">Supprimer définitivement cette société ?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-slate-700">
                <p className="font-semibold text-rose-700">
                  ⚠️ Action irréversible : toutes les données associées seront supprimées.
                </p>
                <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm">
                  <div className="font-medium text-rose-900 mb-2">Seront définitivement effacés :</div>
                  <ul className="list-disc pl-5 space-y-0.5 text-rose-900/90">
                    <li>Comptes admin & agent de la société</li>
                    <li>Propriétaires, maisons, locataires</li>
                    <li>Paiements, dépenses, historique</li>
                    <li>Configuration, contrats de bail personnalisés</li>
                    <li>Compteurs de reçus et demandes d'inscription liées</li>
                  </ul>
                </div>
                <p className="text-xs text-slate-500">
                  Cette opération ne peut pas être annulée. Assurez-vous d'avoir sauvegardé les données si nécessaire.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-delete-society">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="btn-confirm-delete-society">
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!credentials} onOpenChange={(o) => !o && setCredentials(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-new-admin-credentials">
          <DialogHeader>
            <DialogTitle>Identifiants admin générés</DialogTitle>
          </DialogHeader>
          {credentials && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Un compte administrateur a été créé pour <strong>{credentials.nom}</strong>.
                Transmettez ces identifiants à votre client.
              </p>
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700 mb-1">Email</div>
                  <code className="block text-sm font-mono text-slate-900 break-all select-all" data-testid="new-admin-email">{credentials.email}</code>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700 mb-1">Mot de passe par défaut</div>
                  <code className="block text-sm font-mono text-slate-900 select-all" data-testid="new-admin-password">{credentials.password}</code>
                </div>
              </div>
              <div className="text-xs text-rose-600 font-medium">
                ⚠ Notez ces identifiants maintenant — ils ne seront plus affichés.
                Le client devra changer son mot de passe dès la première connexion.
              </div>
              <Button
                onClick={() => {
                  navigator.clipboard?.writeText(`Email: ${credentials.email}\nMot de passe: ${credentials.password}`);
                  toast.success("Identifiants copiés");
                }}
                className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                data-testid="btn-copy-admin-credentials"
              >
                Copier les identifiants
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Restore Database Dialog */}
      <Dialog open={restoreOpen} onOpenChange={(o) => { if (!restoring) setRestoreOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="dialog-restore-db">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <DatabaseBackup className="w-5 h-5 text-amber-600" />
              Restaurer la base de données
            </DialogTitle>
          </DialogHeader>

          {restoreResult ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 space-y-1">
                <div className="font-semibold">Restauration terminée ✅</div>
                <div>{restoreResult.restored_docs} document(s) restaurés.</div>
                <div className="text-xs text-emerald-700">Source : {restoreResult.source_db} → Cible : {restoreResult.target_db}</div>
              </div>
              <p className="text-xs text-slate-500">Vous allez être déconnecté dans quelques secondes…</p>
            </div>
          ) : !restoreConfirm ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
                <div className="font-semibold mb-1">⚠️ Opération critique</div>
                <div>La restauration <strong>écrase intégralement</strong> la base actuelle. Toutes les données existantes seront supprimées avant réimport.</div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="restore-file">Fichier d'archive (.gz créé par <code className="text-xs">mongodump --archive --gzip</code>)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="restore-file"
                    type="file"
                    accept=".gz"
                    onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                    className="cursor-pointer file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-orange-100 file:text-orange-700 hover:file:bg-orange-200"
                    data-testid="input-restore-file"
                  />
                </div>
                {restoreFile && (
                  <p className="text-xs text-slate-500" data-testid="restore-file-info">
                    <strong>{restoreFile.name}</strong> ({(restoreFile.size / 1024).toFixed(1)} Ko)
                  </p>
                )}
                <p className="text-xs text-slate-500">Taille maximale : 200 Mo.</p>
              </div>

              <DialogFooter className="gap-2 sm:gap-2">
                <Button variant="outline" onClick={() => setRestoreOpen(false)} data-testid="btn-restore-cancel">Annuler</Button>
                <Button
                  disabled={!restoreFile}
                  onClick={() => setRestoreConfirm(true)}
                  className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50"
                  data-testid="btn-restore-next"
                >
                  <Upload className="w-4 h-4 mr-2" /> Continuer
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-rose-300 bg-rose-100 p-4 text-sm text-rose-900 space-y-2">
                <div className="font-bold text-rose-800">Dernière confirmation</div>
                <div>Cette opération va <strong>effacer et remplacer</strong> l'intégralité de la base.</div>
                <div className="text-xs">Fichier : <strong>{restoreFile?.name}</strong></div>
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button variant="outline" onClick={() => setRestoreConfirm(false)} disabled={restoring} data-testid="btn-restore-back">Retour</Button>
                <Button
                  disabled={restoring}
                  onClick={onRestore}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="btn-restore-confirm"
                >
                  {restoring ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Restauration…</>) : (<>Oui, restaurer</>)}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
